import styled from "styled-components";

export const FixedContainer = styled.div`
  height: 100vh;
  overflow: hidden;

  @media (max-width: 860px) {
    height: auto;
    overflow-y: auto;
  }
`;

export const PageWrapper = styled.div`
  background: var(--cream, #F3ECE1);
  min-height: 100vh;
  padding-top: 88px;
  overflow: hidden;

  @media (max-width: 860px) {
    min-height: 100vh;
    overflow-y: auto;
  }
`;

export const Main = styled.main`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const NotFoundSection = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(32px, 6vw, 96px);
  align-items: center;
  height: calc(100vh - 88px);
  padding-inline: 0;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 8px;
    height: auto;
    min-height: calc(100vh - 72px);
    padding-block: 32px 56px;
  }
`;

export const Content = styled.div``;

export const Eyebrow = styled.span`
  font: 500 12px 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--red, #D9442B);
`;

export const Title = styled.h1`
  margin: 18px 0 0;
  font: 400 clamp(52px, 6.4vw, 92px) / 0.95 'Instrument Serif', Georgia, serif;
  letter-spacing: -0.02em;
  max-width: 9em;

  em {
    color: var(--red, #D9442B);
  }

  @media (max-width: 860px) {
    font-size: clamp(44px, 12vw, 60px);
    margin-top: 14px;
  }
`;

export const Description = styled.p`
  margin: 24px 0 0;
  font-size: 20px;
  color: var(--body, #4A453D);
  max-width: 26em;

  @media (max-width: 860px) {
    font-size: 18px;
    margin-top: 16px;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 32px;
  margin-top: 36px;

  @media (max-width: 860px) {
    margin-top: 28px;
    gap: 16px 24px;
  }
`;

export const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 17px 26px;
  background: var(--ink, #1A1814);
  color: var(--cream, #F3ECE1);
  font: 500 15px 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  transition: gap 0.2s ease;
  text-decoration: none;

  span {
    color: var(--red, #D9442B);
  }

  &:hover {
    gap: 18px;
  }

  @media (max-width: 860px) {
    width: 100%;
    justify-content: center;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const TextLink = styled.a`
  font: 500 15px 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  border-bottom: 1.5px solid var(--red, #D9442B);
  padding-bottom: 3px;
  text-decoration: none;
  color: var(--ink, #1A1814);
`;

export const ArtContainer = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  max-width: 560px;
  width: 100%;
  justify-self: end;

  @media (max-width: 860px) {
    order: -1;
    justify-self: stretch;
    max-width: none;
    aspect-ratio: auto;
    display: flex;
    flex-direction: column;
  }
`;

export const NumberDisplay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 4%;
  text-align: center;
  font: italic 400 clamp(150px, 19vw, 280px) / 1 'Instrument Serif', Georgia, serif;
  color: var(--ink, #1A1814);
  letter-spacing: -0.04em;

  @media (max-width: 860px) {
    position: static;
    font-size: clamp(120px, 36vw, 200px);
    text-align: left;
  }
`;

export const ArtSVG = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;

  .line {
    fill: none;
    stroke: var(--red, #D9442B);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .draw {
    stroke-dasharray: var(--len);
    stroke-dashoffset: var(--len);
    animation: nfdraw 2.4s ease-out 0.2s forwards;
  }

  @keyframes nfdraw {
    to {
      stroke-dashoffset: 0;
    }
  }

  .start {
    fill: var(--ink, #1A1814);
  }

  .sq {
    fill: none;
    stroke: var(--ink, #1A1814);
    stroke-width: 2.5;
  }

  .dot {
    fill: var(--red, #D9442B);
  }

  @media (max-width: 860px) {
    position: static;
    width: 100%;
    height: auto;
    margin-top: -8px;
  }

  @media (prefers-reduced-motion: reduce) {
    .draw {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
`;

export const QuickLinks = styled.nav`
  display: none;
`;

export const QuickLink = styled.a`
  display: block;
  padding: 24px 24px 28px 0;
  text-decoration: none;

  & + & {
    padding-left: 24px;
    border-left: 1px solid var(--rule, #D2C8B8);
  }

  @media (max-width: 860px) {
    padding: 20px 0 22px;

    & + & {
      padding-left: 0;
      border-left: 0;
      border-top: 1px solid var(--rule, #D2C8B8);
    }
  }
`;

export const QuickLinkLabel = styled.span`
  font: 400 11px 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--red, #D9442B);
`;

export const QuickLinkTitle = styled.b`
  display: flex;
  justify-content: space-between;
  font: 400 30px / 1.1 'Instrument Serif', Georgia, serif;
  margin-top: 8px;

  span {
    color: var(--red, #D9442B);
    transition: transform 0.2s ease;
  }

  ${QuickLink}:hover & span {
    transform: translateX(5px);
  }

  @media (max-width: 860px) {
    font-size: 26px;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      transition: none;
    }
  }
`;

export const QuickLinkDesc = styled.small`
  display: block;
  margin-top: 6px;
  font-size: 16px;
  color: var(--muted, #6B645A);
`;
